import type { Ref } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistance } from 'date-fns';
import {
  listAgentDialogs,
  listJournalEntries,
} from '@/modules/personal-agent-api';
import { track } from '@/shared/utils/track';
import Button from '@/shared/ui/Button';
import SpinnerSan from '@/shared/ui/SpinnerSan';

const LIST_LIMIT = 50;

// Both lists are live uncached reads — gcTime 0 so every open refetches (a
// note saved or a chat held elsewhere must show up). `listRef` reaches the
// list's scroll container for a host that locks page scrolling to it.
export default function AgentHistory({
  now,
  onBack,
  onClose,
  onOpenDialog,
  listRef,
}: {
  now: number;
  onBack: () => void;
  onClose: () => void;
  onOpenDialog: (id: string) => void;
  listRef?: Ref<HTMLDivElement>;
}) {
  const journal = useQuery({
    queryKey: ['agent-history-journal'],
    queryFn: () => listJournalEntries(LIST_LIMIT),
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
  const dialogs = useQuery({
    queryKey: ['agent-history-dialogs'],
    queryFn: async () => {
      const rows = await listAgentDialogs(LIST_LIMIT);
      return rows
        .slice()
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const pending = journal.isPending || dialogs.isPending;
  const failed = journal.isError || dialogs.isError;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2xl">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-start gap-s">
          <button
            type="button"
            className="btn-reset text-gray-text"
            aria-label="Back to chat"
            onClick={onBack}
          >
            <ArrowLeft size={20} />
          </button>
          <p className="text-body-lg font-bold text-gray-text">History</p>
        </div>
        <button
          type="button"
          className="btn-reset grid size-8 place-items-center rounded-xs bg-background-main text-gray-text"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>
      {pending ?
        <div className="grid flex-1 place-items-center">
          <SpinnerSan size={32} />
        </div>
      : failed ?
        <div className="flex flex-col items-start gap-m">
          <p className="text-body-md text-gray">
            Could not load history — please try again.
          </p>
          <Button
            size="sm"
            onClick={() => {
              if (journal.isError) void journal.refetch();
              if (dialogs.isError) void dialogs.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      : <div
          ref={listRef}
          className="flex min-h-0 flex-1 flex-col gap-4xl overflow-y-auto"
        >
          {journal.data.length > 0 && (
            <div className="flex flex-col gap-s">
              <p className="text-body-md font-medium text-gray">Journal</p>
              <div className="flex flex-col gap-l">
                {journal.data.map((entry) => (
                  <div key={entry.id} className="flex flex-col gap-xs">
                    <div className="flex flex-col gap-0.5">
                      {entry.pair !== null && (
                        <p className="text-body-md font-bold text-gray-text">
                          {assetOf(entry.pair)}
                        </p>
                      )}
                      <p className="truncate text-body-md font-medium text-gray-text">
                        {entry.content}
                      </p>
                    </div>
                    <p className="text-body-sm font-medium text-gray">
                      {formatDistance(new Date(entry.createdAt), now, {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {dialogs.data.length > 0 && (
            <div className="flex flex-col gap-s">
              <p className="text-body-md font-medium text-gray">
                AI Chat History
              </p>
              <div className="flex flex-col gap-s">
                {dialogs.data.map((dialog) => (
                  <button
                    key={dialog.id}
                    type="button"
                    className="btn-reset flex flex-col items-start gap-0.5 text-left"
                    onClick={() => {
                      track('ai_history_dialog_opened');
                      onOpenDialog(dialog.id);
                    }}
                  >
                    <p className="w-full truncate text-body-md font-medium text-gray-text">
                      {dialog.title ?? 'New conversation'}
                    </p>
                    <p className="text-body-sm font-medium text-gray">
                      {format(new Date(dialog.updatedAt), 'dd.MM.yyyy')}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {journal.data.length === 0 && dialogs.data.length === 0 && (
            <p className="text-body-md text-gray">No history yet.</p>
          )}
        </div>
      }
    </div>
  );
}

// The design labels a note by its asset ("BTC"); the API stores the pair
// ("BTC/USD") — a pair without a quote shows as is.
function assetOf(pair: string): string {
  return pair.replace(/\/.*$/, '');
}
